import React, { useEffect, useState } from "react";
import {
  FaArrowDown,
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaUserCheck,
  FaVoteYea,
  FaChartBar,
} from "react-icons/fa";
import { GiKimono } from "react-icons/gi";

const slides = [
  {
    src: "/images/illustrations/illustration_home_001.webp",
    title: "Bienvenue sur l’Application Ligue NA",
    subtitle: "Clubs, arbitres, enseignants et judokas",
    accent: "from-emerald-400 to-lime-300",
    icon: <FaUserCheck className="text-white text-xl" />,
  },
  {
    src: "/images/illustrations/illustration_home_002.webp",
    title: "Votes électroniques",
    subtitle: "Pour les responsables et les instances",
    accent: "from-sky-400 to-blue-500",
    icon: <FaVoteYea className="text-white text-xl" />,
  },
  {
    src: "/images/illustrations/illustration_home_003.webp",
    title: "Calendrier des compétitions",
    subtitle: "Ligue et comités départementaux",
    accent: "from-violet-400 to-fuchsia-500",
    icon: <FaCalendarAlt className="text-white text-xl" />,
  },
  {
    src: "/images/illustrations/illustration_home_004.webp",
    title: "Arbitrage",
    subtitle: "Règlements, ressources et quizz",
    accent: "from-orange-400 to-amber-500",
    icon: <FaChartBar className="text-white text-xl" />,
  },
  {
    src: "/images/illustrations/illustration_home_005.webp",
    title: "Techniques judo",
    subtitle: "Vidéos et révisions techniques",
    accent: "from-yellow-300 to-orange-400",
    icon: <GiKimono className="text-white text-xl" />,
  },
];

function HomeHeroSection() {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  const nextSlide = () => {
    setCurrentSlideIndex((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlideIndex((prev) =>
      prev === 0 ? slides.length - 1 : prev - 1
    );
  };

  const scrollToNextSection = () => {
  const nextSection = document.getElementById("quick-shortcuts");
  if (nextSection) {
    nextSection.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }
};

  useEffect(() => {
    const autoSlide = setInterval(() => {
      setCurrentSlideIndex((prev) => (prev + 1) % slides.length);
    }, 7000);

    return () => clearInterval(autoSlide);
  }, []);

  const currentSlide = slides[currentSlideIndex];

  return (
    <header className="relative isolate flex min-h-[100svh] items-center overflow-hidden bg-slate-950">
      {/* Images */}
      {slides.map((slide, index) => (
        <img
          key={slide.src}
          src={slide.src}
          alt={slide.title}
          className={`absolute inset-0 h-full w-full object-cover transition-all duration-1000 ${
            index === currentSlideIndex
              ? "scale-100 opacity-100"
              : "scale-105 opacity-0"
          }`}
        />
      ))}

      {/* Overlays légers */}
      <div className="absolute inset-0 bg-black/25" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/20 to-black/10" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/55 to-transparent" />

      {/* Contenu */}
      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pt-28 pb-28 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white/90 backdrop-blur-sm">
            <span
              className={`inline-flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br ${currentSlide.accent}`}
            >
              {currentSlide.icon}
            </span>
            Ligue Judo Nouvelle-Aquitaine
          </div>

          <h1 className="text-4xl font-black leading-tight text-white drop-shadow-md sm:text-5xl lg:text-6xl">
            {currentSlide.title}
          </h1>

          <p className="mt-3 text-base text-white/85 sm:text-lg">
            {currentSlide.subtitle}
          </p>

          <div className="mt-6">
            <button
              onClick={scrollToNextSection}
              className="inline-flex items-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-semibold text-slate-900 shadow-lg transition hover:-translate-y-0.5 hover:bg-slate-100"
            >
              Découvrir la suite
              <FaArrowDown className="text-xs" />
            </button>
          </div>
        </div>
      </div>

      {/* Barre de navigation basse */}
      <div className="absolute bottom-6 left-0 right-0 z-20">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* Dots */}
          <div className="flex items-center gap-2">
            {slides.map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setCurrentSlideIndex(index)}
                aria-label={`Aller au slide ${index + 1}`}
                className={`h-2.5 rounded-full transition-all ${
                  index === currentSlideIndex
                    ? "w-8 bg-white"
                    : "w-2.5 bg-white/45 hover:bg-white/75"
                }`}
              />
            ))}
          </div>

          {/* Flèche descente */}
          <button
            onClick={scrollToNextSection}
            className="group inline-flex items-center gap-2 text-white/85 transition hover:text-white"
            aria-label="Descendre vers la section suivante"
          >
            <span className="hidden text-sm font-medium sm:inline">
              Descendre
            </span>
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/10 backdrop-blur-sm transition group-hover:bg-white/15">
              <FaArrowDown className="animate-bounce text-sm" />
            </span>
          </button>

          {/* Chevrons déplacés en bas à droite */}
          <div className="flex items-center gap-2">
            <button
              onClick={prevSlide}
              aria-label="Slide précédent"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-black/25 text-white backdrop-blur-sm transition hover:bg-black/40"
            >
              <FaChevronLeft />
            </button>

            <button
              onClick={nextSlide}
              aria-label="Slide suivant"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-black/25 text-white backdrop-blur-sm transition hover:bg-black/40"
            >
              <FaChevronRight />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}

export default HomeHeroSection;